import React, { useState } from 'react';
import { TerminalSquare, ChevronDown, ChevronUp, CheckCircle2, XCircle, AlertTriangle, MinusCircle } from 'lucide-react';

/**
 * Dev-only surface for the Website audit's AI Optimization diagnostics.
 *
 * The AI Search Visibility card falls back to its empty-state copy whenever the
 * audit's `aiSearch` block has 0 keyword volume and 0 brand mentions — which
 * looks identical whether the DataForSEO AI Optimization endpoints aren't on the
 * plan (api_error), the call was skipped (no keywords), or there genuinely is no
 * AI data. `fetchAiSearchData` records each call's outcome in
 * `aiSearch.diagnostics`; this panel renders it so the cause is visible.
 *
 * Only mounted when dev mode is on (see useDevMode) — clients never see it.
 */

const StatusPill = ({ status }) => {
  const map = {
    ok: { cls: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300', Icon: CheckCircle2 },
    api_error: { cls: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300', Icon: XCircle },
    error: { cls: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300', Icon: XCircle },
    skipped: { cls: 'bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-400', Icon: MinusCircle },
  };
  const { cls, Icon } = map[status] || map.skipped;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${cls}`}>
      <Icon className="w-3 h-3" />
      {status || 'unknown'}
    </span>
  );
};

const CallRow = ({ label, call }) => {
  if (!call) return null;
  return (
    <tr className="align-top">
      <td className="pr-3 py-1 text-gray-600 dark:text-gray-400 whitespace-nowrap">{label}</td>
      <td className="pr-3 py-1"><StatusPill status={call.status} /></td>
      <td className="py-1 break-all">
        {call.status === 'skipped' && <span className="text-gray-500">{call.reason || 'skipped'}</span>}
        {call.status === 'error' && <span className="text-red-600 dark:text-red-400">{call.message || 'request failed'}</span>}
        {(call.status === 'ok' || call.status === 'api_error') && (
          <span>
            code {call.statusCode ?? '—'}
            {call.statusMessage ? ` · ${call.statusMessage}` : ''}
            {typeof call.items === 'number' ? ` · ${call.items} item${call.items !== 1 ? 's' : ''}` : ''}
          </span>
        )}
      </td>
    </tr>
  );
};

const AiSearchDevDiagnostics = ({ aiSearch }) => {
  const [open, setOpen] = useState(true);
  const diag = aiSearch?.diagnostics || null;

  const hasApiError =
    diag &&
    [diag.keywordVolume, diag.brandMentions, diag.competitorDomains].some(
      (c) => c && (c.status === 'api_error' || c.status === 'error')
    );

  return (
    <div className="mt-6 rounded-xl border-2 border-amber-300 dark:border-amber-700 bg-amber-50/60 dark:bg-amber-900/10 p-4 text-xs font-mono">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-center gap-2 text-amber-800 dark:text-amber-300 font-semibold"
      >
        <TerminalSquare className="w-4 h-4" />
        <span className="text-center">Dev Diagnostics — AI Search</span>
        {hasApiError && <AlertTriangle className="w-4 h-4 text-red-500" />}
        {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>

      {open && (
        <div className="mt-3 text-gray-800 dark:text-gray-200">
          {!aiSearch ? (
            <p className="text-red-600 dark:text-red-400">
              aiSearch is null on this audit — the whole AI fetch threw (or this audit predates AI data). Check the
              cloud-function logs for &quot;AI search data fetch failed&quot;.
            </p>
          ) : !diag ? (
            <p className="text-gray-500 dark:text-gray-400">
              No diagnostics recorded on this audit (it likely ran before diagnostics were added). Re-run the audit to
              capture them.
            </p>
          ) : (
            <>
              <div className="flex flex-wrap gap-x-6 gap-y-1 text-[11px] mb-2">
                <span>keywords sent: {diag.keywordsSent ?? 0}</span>
                <span>primary keyword: {diag.primaryKeyword || '— (none)'}</span>
                <span>total AI volume: {(aiSearch.totalAiVolume || 0).toLocaleString()}</span>
                <span>brand mentions: {aiSearch.llmMentions?.totalMentions || 0}</span>
                {diag.fetchedAt && <span>fetched: {diag.fetchedAt.slice(0, 19).replace('T', ' ')}</span>}
              </div>
              <table className="w-full text-[11px]">
                <thead>
                  <tr className="text-left text-gray-500">
                    <th className="pr-3 font-medium">Call</th>
                    <th className="pr-3 font-medium">Status</th>
                    <th className="font-medium">Detail</th>
                  </tr>
                </thead>
                <tbody>
                  <CallRow label="Keyword volume" call={diag.keywordVolume} />
                  <CallRow label="Brand mentions" call={diag.brandMentions} />
                  <CallRow label="Competitor domains" call={diag.competitorDomains} />
                </tbody>
              </table>
              {hasApiError && (
                <p className="mt-2 text-red-600 dark:text-red-400">
                  ⚠ An <code>api_error</code> usually means the DataForSEO AI Optimization product isn&apos;t enabled on
                  the account (or the endpoint/params were rejected). A <code>skipped</code> keyword-volume call means no
                  keywords were available to send. A clean <code>ok</code> with 0 items means there genuinely is no AI
                  data for this domain/keywords yet.
                </p>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default AiSearchDevDiagnostics;
